import React, { useEffect, useRef, useState } from 'react'
import { Box, Flex, Text } from '@chakra-ui/core'
import { css, keyframes } from '@emotion/core'
import { IPop } from 'constants/interfaces'
import { PopCreatorText } from '../../PopCreatorText'
import { MarkUpText } from '../../MarkUpText'
import { useSelector } from 'react-redux'
import { isRtlSelector } from 'redux/selectors/general'
import { STOREE_AI_UID } from 'constants/baseValues'
import useLocaleText from 'components/useLocaleText'

// ── Typewriter hook ────────────────────────────────────────────────────────────
// Phases: typing → pause-full → instant clear → typing again
type Phase = 'typing' | 'pause-full'

const TYPING_SPEED_MS = 38
const PAUSE_FULL_MS = 2800

const useTypewriter = (text: string) => {
  const [displayedText, setDisplayedText] = useState('')
  const [isTyping, setIsTyping] = useState(true)

  const phaseRef = useRef<Phase>('typing')
  const indexRef = useRef(0)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    // Reset on text change
    setDisplayedText('')
    setIsTyping(true)
    phaseRef.current = 'typing'
    indexRef.current = 0

    const tick = () => {
      const phase = phaseRef.current

      if (phase === 'typing') {
        indexRef.current++
        setDisplayedText(text.slice(0, indexRef.current))

        if (indexRef.current >= text.length) {
          phaseRef.current = 'pause-full'
          setIsTyping(false)
          timerRef.current = setTimeout(tick, PAUSE_FULL_MS)
        } else {
          timerRef.current = setTimeout(tick, TYPING_SPEED_MS)
        }
      } else if (phase === 'pause-full') {
        // Instant clear, then restart typing
        indexRef.current = 0
        setDisplayedText('')
        setIsTyping(true)
        phaseRef.current = 'typing'
        timerRef.current = setTimeout(tick, TYPING_SPEED_MS)
      }
    }

    // Small initial delay so the modal finishes its entrance animation
    timerRef.current = setTimeout(tick, 400)

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [text])

  return { displayedText, isTyping }
}

// ── Cursor blink animation ─────────────────────────────────────────────────────
const cursorBlinkKeyframes = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0; }
`

const cursorCss = css`
  animation: ${cursorBlinkKeyframes} 0.8s step-end infinite;
  display: inline-block;
  margin-left: 2px;
  font-weight: 400;
  line-height: 1;
`

// ── Component ──────────────────────────────────────────────────────────────────
interface IProps {
  aiPop: IPop
}

export const AIPopBody = ({ aiPop }: IProps) => {
  const { pop_title, pop_initiator } = aiPop
  const isRtl = useSelector(isRtlSelector)
  const t_topic = useLocaleText('topic')

  const { displayedText, isTyping } = useTypewriter(pop_title)

  return (
    <Flex flexDir="column" alignItems="center" px="65px" textAlign="center" pt="60px" flex="1" height="100%">
      <PopCreatorText created_by={STOREE_AI_UID} />
      <Flex pt="40px" w="100%" alignItems="baseline" dir={isRtl ? 'rtl' : 'ltr'} style={{ gap: '8px' }}>
        <Text as="span" fontSize="24px" flexShrink={0} style={{ whiteSpace: 'nowrap' }}>
          {t_topic}
        </Text>
        <Box
          flex={1}
          minW={0}
          overflow="hidden"
          textAlign={isRtl ? 'right' : 'left'}
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
          }}
        >
          <MarkUpText
            text={displayedText}
            initiator={pop_initiator}
            fontFamily="Arial"
            lineHeight="25px"
            fontSize="24px"
            fontWeight="400"
          />
          {isTyping && (
            <Box as="span" css={cursorCss} fontSize="24px" fontFamily="Arial" color="currentColor">
              |
            </Box>
          )}
        </Box>
      </Flex>
    </Flex>
  )
}
